import { kebabCase } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import SimpleCard from '@pflegia/ui/shared/simple-card';

import { getTargetImageUrl } from '@domains/landing-page/utils/get-target-image-url';

interface AdvantageCardProps {
  advantage?: AdvantageTranslationObject;
  altTextShown?: boolean;
  isLoadedTranslation?: boolean;
  name?: string;
  namespace?: string;
  translationValues?: Record<string, unknown>;
}

export type AdvantageTranslationObject = {
  title: string;
  description: string;
  nameInBucket?: string;
};

const feesImage = '/assets/advantages/fees.svg';

const AdvantageCard: React.FC<AdvantageCardProps> = ({
  advantage,
  altTextShown,
  isLoadedTranslation,
  name,
  namespace = 'landingPage',
  translationValues,
}) => {
  const { t } = useTranslation(namespace, { keyPrefix: 'advantages' });
  const { sourceDomain } = useFlavorFromRoute();

  const descriptionKey = sourceDomain === SourceDomain.PraxiaDe ? `${name}.praxia.description` : `${name}.description`;
  const titleKey = sourceDomain === SourceDomain.PraxiaDe ? `${name}.praxia.title` : `${name}.title`;
  const translatedName = t(titleKey);
  const altText = t(`${name}.altText`);
  const { title, description } = advantage ?? {};

  const translationProps = isLoadedTranslation ? { children: description } : { i18nKey: descriptionKey, t, values: translationValues };

  const [advantageImageUrl, setAdvantageImageUrl] = useState(feesImage);
  useEffect(() => {
    const loadImage = async () => {
      const srcSet: string[] = [];
      if (advantage?.nameInBucket) srcSet.push(advantage.nameInBucket);
      if (!isLoadedTranslation) srcSet.push(`/assets/advantages/${kebabCase(name)}.svg`);
      srcSet.push(feesImage);
      setAdvantageImageUrl(await getTargetImageUrl(...srcSet));
    };
    loadImage();
  }, [advantage?.nameInBucket, isLoadedTranslation, name]);

  return (
    <SimpleCard className='flex flex-row p-4 text-sm md:flex-col md:px-6 md:py-8 md:text-xl'>
      {advantageImageUrl ? (
        <Image
          alt={title ?? (altTextShown ? `${translatedName} - ${altText}` : '')}
          className='m-2'
          height={90}
          src={advantageImageUrl}
          width={90}
        />
      ) : null}
      <div className='flex flex-col justify-center gap-2 overflow-hidden break-words'>
        <h4 className='text-xl font-medium md:text-3xl'>{isLoadedTranslation ? title : translatedName}</h4>
        <p>
          <Trans {...translationProps} components={{ span: <span className='font-bold' /> }} />
        </p>
      </div>
    </SimpleCard>
  );
};

export default AdvantageCard;
